import React, { useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useAtom } from 'jotai';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { activeChildDetail, parentProfile } from '../../atomstore/store';
import { useRequestProcessor } from '../../requestProcessor';
import { getVisitorPickupHistory } from '../../queryfunctions/queryfunctions';
import SpinnerLoading from '../common/SpinnerLoading';

const EVENT_COLORS = {
    created: 'bg-cyan-700',
    card_created: 'bg-cyan-700',
    requested: 'bg-amber-600',
    pickup_requested: 'bg-amber-600',
    activated: 'bg-amber-600',
    scheduled: 'bg-amber-600',
    deactivated: 'bg-red-500',
    cancelled: 'bg-red-500',
    canceled: 'bg-red-500',
    completed: 'bg-emerald-600',
    checkout: 'bg-emerald-600',
    pickup_completed: 'bg-emerald-600',
    otp_verified: 'bg-emerald-600',
    otp_sent: 'bg-white border-2 border-gray-300',
};

const formatHistoryTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date).replace(',', ' ·').toUpperCase();
};

const humanizeEventType = (type) => {
    if (!type) return 'Pickup update';
    return String(type)
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const mapHistoryEvent = (item, index) => {
    const type = String(item?.event_type || item?.type || item?.action || '').toLowerCase();
    const actorRaw = String(item?.actor || item?.created_by || item?.by || item?.source || '');
    const actor = /center|staff|admin/i.test(actorRaw) ? 'Center' : (actorRaw || 'Parent');
    const title =
        item?.title ||
        item?.message ||
        item?.description ||
        item?.event ||
        humanizeEventType(type);

    return {
        id: String(item?.id ?? item?.event_id ?? `${type || 'event'}-${index}`),
        title,
        actor: /center/i.test(actor) ? 'Center' : 'Parent',
        timestamp: item?.display_time || item?.timestamp_display || formatHistoryTimestamp(
            item?.timestamp || item?.created_at || item?.event_time || item?.created
        ),
        color: EVENT_COLORS[type] || (/center/i.test(actor) ? 'bg-emerald-600' : 'bg-amber-600'),
    };
};

const PickupAuthorizationHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { personId } = useParams();
    const { useQueryWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [parent] = useAtom(parentProfile);
    const parentEmail =
        parent?.parentEmail ||
        (activeChild?.parent === 'Mother' ? activeChild?.mother_email : activeChild?.father_email) ||
        '';
    const cardId = personId || location.state?.person?.id || '';

    const historyQuery = useQueryWrapper(
        ['visitorPickupHistory', activeChild?.id, parentEmail, cardId],
        () => getVisitorPickupHistory(activeChild, parentEmail, cardId),
        {
            enabled: !!activeChild?.id && !!parentEmail && !!cardId,
            refetchOnWindowFocus: false,
        }
    );

    const historyPayload = useMemo(() => {
        if (historyQuery.data?.status !== 'ok' && historyQuery.data?.status !== 'success') return null;
        return historyQuery.data.data || historyQuery.data.response || null;
    }, [historyQuery.data]);

    const personName =
        historyPayload?.card?.visitor_name ||
        historyPayload?.visitor?.visitor_name ||
        historyPayload?.visitor_name ||
        location.state?.person?.name ||
        'Pickup person';

    const events = useMemo(() => {
        const source =
            historyPayload?.history ||
            historyPayload?.events ||
            historyPayload?.timeline ||
            historyPayload?.logs ||
            [];
        return (Array.isArray(source) ? source : []).map(mapHistoryEvent);
    }, [historyPayload]);

    const historyError =
        !cardId
            ? 'Card id is missing.'
            : !activeChild?.id
                ? 'Child id is missing.'
                : !parentEmail
                    ? 'Parent email is missing.'
                    : historyQuery.data?.status === 'error'
                        ? historyQuery.data.error || 'Unable to load visitor pickup history.'
                        : historyQuery.error?.message || (historyQuery.isError ? 'Unable to load visitor pickup history.' : '');

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50">
            <div className="sticky top-0 z-20 flex items-center justify-between border-b bg-white p-4">
                <div className="flex min-w-0 items-center space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="shrink-0 rounded-full p-1 text-gray-600 hover:bg-gray-100 active:bg-gray-100"
                        aria-label="Go back"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                    <h2 className="cursor-pointer truncate text-lg font-bold text-gray-800" onClick={() => navigate(-1)}>
                        {personName}
                    </h2>
                </div>
                <span className="ml-3 shrink-0 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                    History
                </span>
            </div>

            {historyQuery.isFetching && <SpinnerLoading />}

            <main className="p-4 lg:p-4">
                {historyQuery.isFetching && !historyPayload
                    ? null
                    : historyError && !historyPayload
                        ? null
                        : events.length === 0 ? (
                    <section className="flex min-h-[50vh] flex-col items-center justify-center px-5 text-center">
                        <h2 className="text-xl font-bold text-gray-900">No history yet</h2>
                        <p className="mt-2 max-w-sm text-sm leading-6 text-gray-600">
                            Pickup activity for this person will appear here.
                        </p>
                    </section>
                ) : (
                    <div className="relative">
                        <div
                            className="absolute bottom-7 left-[7px] top-2 w-px bg-gray-300"
                            aria-hidden="true"
                        />

                        {events.map((event, index) => (
                            <article
                                key={event.id}
                                className={`relative pl-9 ${index === events.length - 1 ? '' : 'pb-6'}`}
                            >
                                <span
                                    className={`absolute left-0 top-1.5 h-[15px] w-[15px] rounded-full ring-4 ring-gray-50 ${event.color}`}
                                    aria-hidden="true"
                                />
                                <h2 className="text-base font-medium leading-6 text-gray-800">
                                    {event.title}
                                </h2>
                                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                    <span className={`rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                        event.actor === 'Center'
                                            ? 'border-cyan-300 bg-cyan-50 text-cyan-700'
                                            : 'border-gray-300 bg-white text-gray-600'
                                    }`}>
                                        {event.actor}
                                    </span>
                                    {event.timestamp ? (
                                        <time className="text-[11px] font-medium uppercase tracking-[0.12em] text-gray-400">
                                            {event.timestamp}
                                        </time>
                                    ) : null}
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default PickupAuthorizationHistory;
